import { View } from "ui/atoms/view";
import { UrgentTasks } from "../organisms/UrgentTasks";
import { OverdueTasks } from "../organisms/OverdueTasks";
import { DueToday } from "../organisms/DueToday";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { Button, ButtonText } from "ui/gluestack/button";
import { useHandleSubmitShiftHandover } from "../hooks/useHandleSubmitShiftHandover";
import { ShiftAccordion } from "../organisms/ShiftAccordion";
import Pagination from "ui/molecules/pagination/Pagination";
import { FlatList, Text } from "react-native";
import { ShiftHandoverTaskClass } from "../../../services/api/types/shiftHandover/classes/ShiftHandoverTaskClass";
import { CurrentShiftAccordion } from "../organisms/CurrentShiftAccordion";
import { useEffect, useState } from "react";
import { FormEnums } from "app/services/api/types/forms/enums/FormEnums";
import { HandoverSection } from "../molecules/HandoverSection";
import { LogSection } from "../organisms/LogSection";

import { HandoverAssessmentHistory } from "../organisms/HandoverAssessmentHistory";
import { useHandleShiftHandovers } from "../hooks/useHandleShiftHandovers";
import { ShiftHandoverIndexModel } from "../../../services/api/types/shiftHandover/models/ShiftHandoverIndexModel";
import { ResidentShiftHandoverResponse } from "../../../services/api/types/shiftHandover/responses/ResidentShiftHandoverResponse";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { useCalendarEventTypes } from "app/features/calendar/hooks/useCalendarEventTypes";

interface Props {
  residentId: number;
}
export function ResidentShiftHandoverTemplate({ residentId }: Props) {
  const [titleValue, setTitleValue] = useState<string>("");
  const { friendlyNames } = useCalendarEventTypes();

  const { submit } = useHandleSubmitShiftHandover({
    residentId,
    titleValue,
    onSuccess: () => {
      setTitleValue("");
    },
  });

  const {
    indexData,
    isIndexLoading,
    parsedOverdueAppointments,
    parsedDueTodayAppointments,
    handoverData,
    isHandoverLoading,
    fluidIntakeLogs,
    fluidOutputLogs,
    bowelMovementLogs,
    assessmentHistoryLogs,
    handlePageChange,
  } = useHandleShiftHandovers({
    residentId,
  });

  const isShiftHandoverIndexModel = (
    data: ShiftHandoverIndexModel | ResidentShiftHandoverResponse,
  ): data is ShiftHandoverIndexModel => {
    return data && "title" in data;
  };

  useEffect(() => {
    if (handoverData && isShiftHandoverIndexModel(handoverData)) {
      setTitleValue(handoverData.title || "");
    }
  }, [handoverData]);

  if (!handoverData || !indexData || isHandoverLoading || isIndexLoading) {
    return <LoadingSpinner />;
  }

  return (
    <View className="gap-8">
      <View className="pb-2">
        <CurrentShiftAccordion
          titleValue={titleValue}
          onChangeTitle={setTitleValue}
        >
          <UrgentTasks data={handoverData?.urgent_concerns} isEditing />
          <OverdueTasks data={parsedOverdueAppointments} isEditing />
          <DueToday data={parsedDueTodayAppointments} isEditing />
          <HandoverSection
            title="Daily Assessment"
            titleClassNames="text-primary-800"
          >
            <Text className="text-base text-primary-400 ">State Reporting</Text>
            <HandoverAssessmentHistory
              tag={FormEnums.RESIDENT_STATE_REPORTING}
              data={handoverData.submitted_forms}
              href={`/residents/${residentId}/reports/resident-state-reporting`}
            />

            <Text className="text-base text-primary-400 ">Routine</Text>
            <HandoverAssessmentHistory
              tag={FormEnums.DAILY_REPORTING}
              data={handoverData.submitted_forms}
              href={`/residents/${residentId}/reports/daily-reporting`}
            />

            <Text className="text-base text-primary-400">Vital Signs</Text>
            <HandoverAssessmentHistory
              tag={FormEnums.VITAL_SIGNS}
              data={handoverData.submitted_forms}
              href={`/residents/${residentId}/parameters`}
            />
            <LogSection
              title="Fluid Intake"
              items={fluidIntakeLogs}
              href={`/residents/${residentId}/reports/fluid-intake-output-report/fluid-intake`}
            />
            <LogSection
              title="Fluid Output"
              items={fluidOutputLogs}
              href={`/residents/${residentId}/reports/fluid-intake-output-report/fluid-output`}
            />
            <LogSection title="Bowel Movement" items={bowelMovementLogs} />
          </HandoverSection>
          <HandoverSection title="Care Plan" titleClassNames="text-primary-800">
            <HandoverAssessmentHistory
              tag={FormEnums.CARE_ASSESSMENT}
              data={handoverData.latest_nurse_care_plan}
              href={`/residents/${residentId}/care-plans`}
            />
          </HandoverSection>
          <PermissionCheck permission={PermissionsEnums.SIGN_SHIFT_HANDOVER}>
            <View className="flex-row justify-end mt-8">
              <Button
                variant="solid"
                action="primary"
                size="md"
                onPress={submit}
              >
                <ButtonText>Sign</ButtonText>
              </Button>
            </View>
          </PermissionCheck>
        </CurrentShiftAccordion>
      </View>

      <FlatList
        scrollEnabled={false}
        data={indexData.data}
        keyExtractor={(item) => item.id.toString()}
        ItemSeparatorComponent={() => <View className="h-4" />}
        renderItem={({ item: handover }) => {
          const logs = assessmentHistoryLogs(handover);

          return (
            <ShiftAccordion
              title={handover.title}
              shiftHandoverId={handover.id}
              submittedAt={handover.submitted_at}
              isSubmitted={true}
              receivedBy={handover.received_by_shift_user}
            >
              <UrgentTasks data={handover.urgent_concerns} />
              <OverdueTasks
                data={ShiftHandoverTaskClass.fromShiftHandoverAppointmentModelsArray(
                  handover.overdue_appointments,
                  friendlyNames,
                )}
              />
              <DueToday
                data={ShiftHandoverTaskClass.fromShiftHandoverAppointmentModelsArray(
                  handover.due_today_appointments,
                  friendlyNames,
                )}
              />
              <HandoverSection
                title="Daily Assessment"
                titleClassNames="text-primary-800"
              >
                <Text className="text-base text-primary-400 mb-4">
                  State Reporting
                </Text>
                <HandoverAssessmentHistory
                  tag={FormEnums.RESIDENT_STATE_REPORTING}
                  data={handover.submitted_forms}
                  href={`/residents/${residentId}/reports/resident-state-reporting/resident-state-report`}
                />
                <Text className="text-base text-primary-400 mb-4">Routine</Text>
                <HandoverAssessmentHistory
                  tag={FormEnums.DAILY_REPORTING}
                  data={handover.submitted_forms}
                  href={`/residents/${residentId}/reports/daily-reporting`}
                />
                <Text className="text-base text-primary-400 mb-4">
                  Parameters
                </Text>
                <HandoverAssessmentHistory
                  tag={FormEnums.VITAL_SIGNS}
                  data={handover.submitted_forms}
                  href={`/residents/${residentId}/parameters`}
                />
                <LogSection
                  title="Fluid Intake"
                  items={logs.fluidIntakeLogs}
                  href={`/residents/${residentId}/reports/fluid-intake-output-report/fluid-intake`}
                />
                <LogSection
                  title="Fluid Output"
                  items={logs.fluidOutputLogs}
                  href={`/residents/${residentId}/reports/fluid-intake-output-report/fluid-output`}
                />
                <LogSection
                  title="Bowel Movement"
                  items={logs.bowelMovementLogs}
                />
              </HandoverSection>
              <HandoverSection
                title="Care Plan"
                titleClassNames="text-primary-800"
              >
                <HandoverAssessmentHistory
                  tag={FormEnums.CARE_ASSESSMENT}
                  data={handover.latest_nurse_care_plan}
                  href={`/residents/${residentId}/care-plans`}
                />
              </HandoverSection>
            </ShiftAccordion>
          );
        }}
      ></FlatList>

      <Pagination meta={indexData?.meta} onPageChange={handlePageChange} />
    </View>
  );
}
